import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { IMAGES, SERVICES, CAPABILITIES, COMPANY } from "@/lib/site";
import { IndustriesMarquee } from "@/components/sections/IndustriesMarquee";
import { CTABand } from "@/components/sections/CTABand";
import { FeaturedWork } from "@/components/sections/FeaturedWork";
import { InstagramFeed } from "@/components/sections/InstagramFeed";
import { ReviewsBand } from "@/components/sections/ReviewsBand";
import { RecentProjectsTicker } from "@/components/sections/RecentProjectsTicker";
import { SEO, localBusinessSchema } from "@/components/SEO";
import { ArrowRight, Zap, Shield, Truck, Factory } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stats = [
  { k: "8,000", u: "SQ FT", l: "Facility" },
  { k: "20", u: "FEET", l: "Max Part Length" },
  { k: "10", u: "FEET", l: "Max Part Width" },
  { k: "24H", u: "SLA", l: "Quote Response" },
];

export default function Home() {
  const [serviceMedia, setServiceMedia] = useState({});

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/services/media`)
      .then((r) => !cancelled && setServiceMedia(r.data || {}))
      .catch(() => !cancelled && setServiceMedia({}));
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title={null}
        description={`Production-grade powder coating, media blasting, and chemical stripping in ${COMPANY.city}. Parts up to ${COMPANY.maxLength} × ${COMPANY.maxWidth}. Serving general contractors, fabricators, architects, and OEM programs across TN and the Southeast.`}
        image={IMAGES.heroSparks}
        path="/"
        jsonLd={localBusinessSchema()}
      />
      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={IMAGES.heroSparks} alt="Industrial sparks flying" className="w-full h-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#09090B]" />
          <div className="absolute inset-0 grid-lines opacity-40" />
        </div>

        <div className="relative px-6 lg:px-10 pt-24 pb-16 min-h-[92vh] flex flex-col justify-end">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="eyebrow mb-6" data-testid="hero-eyebrow">Hendersonville, Tennessee · Est. Industrial Finishing</div>
              <h1 className="font-display text-[14vw] sm:text-[9vw] lg:text-[7.5vw] leading-[0.85] tracking-tighter">
                Heavy-Duty<br />
                <span className="text-[#FF5722]">Powder Coating.</span><br />
                Built for Volume.
              </h1>
              <p className="mt-8 max-w-xl text-lg text-zinc-300 leading-relaxed">
                {COMPANY.name} delivers production-grade powder coating, media blasting,
                and chemical stripping from an {COMPANY.facilitySqFt} sq ft facility engineered for
                fabricators, manufacturers, GCs, and OEM programs across the Southeast.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/quote" className="btn-primary" data-testid="hero-quote-btn">
                  Request a Quote <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/capabilities" className="btn-ghost" data-testid="hero-capabilities-btn">
                  See Capabilities
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="border border-white/10 bg-black/40 backdrop-blur-sm p-6">
                <div className="eyebrow mb-4">Live Facility Specs</div>
                <div className="grid grid-cols-2 gap-6">
                  {stats.map(s => (
                    <div key={s.l}>
                      <div className="font-display text-4xl md:text-5xl text-white leading-none tracking-tighter">{s.k}</div>
                      <div className="text-[#FF5722] text-xs uppercase tracking-widest mt-1">{s.u}</div>
                      <div className="text-zinc-400 text-xs mt-1">{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <IndustriesMarquee />

      {/* SERVICES */}
      <section className="section px-6 lg:px-10 relative" data-testid="home-services">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">Coatings & Surface Prep</div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tighter">
              Seven Services.<br />
              <span className="text-[#FF5722]">One</span> Coating Partner.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <p className="text-zinc-400 leading-relaxed">
              From production powder coating to architectural weatherable finishes, media blasting,
              and non-destructive chemical stripping — every service runs through the same disciplined
              QC process under one roof.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
          {SERVICES.map((s, i) => {
            const spans = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4"];
            const heroOverride = serviceMedia[s.slug]?.hero_image_url;
            const imgSrc = heroOverride || s.image;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className={`group relative overflow-hidden border border-white/10 ${spans[i] || "lg:col-span-4"} min-h-[320px] flex items-end`}
                data-testid={`home-service-${s.slug}`}
              >
                <img src={imgSrc} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                <div className="relative p-8 w-full">
                  <div className="eyebrow mb-2">{s.tag}</div>
                  <div className="font-display text-3xl md:text-4xl tracking-tighter">{s.title}</div>
                  <div className="mt-4 flex items-center gap-2 text-[#FF5722] text-sm uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                    Explore <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <FeaturedWork />
      <RecentProjectsTicker />
      <InstagramFeed />
      <ReviewsBand />

      {/* VALUE PROPS */}
      <section className="section px-6 lg:px-10 border-t border-white/10 bg-[#0B0B0D]" data-testid="home-value-props">        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">Why American Armor</div>
            <h2 className="font-display text-5xl md:text-6xl leading-[0.9] tracking-tighter">
              We coat what<br /> others <span className="text-[#FF5722]">turn away.</span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
              Long structural members. Complex weldments. Recurring OEM programs. Restoration one-offs.
              Our facility is built to keep every job moving.
            </p>
            <Link to="/about" className="btn-ghost mt-8 inline-flex">About the Facility →</Link>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { i: Factory, t: "Production Capacity", d: "Two ovens, two spray booths, and a walk-in blast room built for volume without bottlenecks." },
              { i: Shield, t: "Documented QC", d: "Adhesion, mil-thickness, and gloss are inspected per lot and traceable to your PO." },
              { i: Zap, t: "Fast Turnaround", d: "Standard runs ship in 5–10 business days. Rush and JIT schedules coordinated with your line." },
              { i: Truck, t: "Regional Reach", d: "Tennessee, Kentucky, Alabama, and Georgia served regularly. Freight coordinated end-to-end." },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="border border-white/10 p-8 hover:border-[#FF5722] transition-colors">
                <Icon className="w-8 h-8 text-[#FF5722]" />
                <div className="font-display text-2xl mt-6 tracking-tight">{t}</div>
                <p className="text-zinc-400 mt-3 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES STRIP */}
      <section className="section px-6 lg:px-10 border-t border-white/10" data-testid="home-capabilities">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-4">Capabilities</div>
            <h2 className="font-display text-5xl md:text-6xl tracking-tighter">Spec Sheet</h2>
          </div>
          <Link to="/capabilities" className="btn-ghost">Full Capabilities →</Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-t border-l border-white/10">
          {CAPABILITIES.map(c => (
            <div key={c.label} className="border-r border-b border-white/10 p-6">
              <div className="text-xs uppercase tracking-widest text-zinc-500">{c.label}</div>
              <div className="font-display text-2xl md:text-3xl mt-2 tracking-tight">{c.value}</div>
            </div>
          ))}
        </div>
      </section>

      <CTABand subtitle="Send drawings, part lists, or photos. We respond within one business day with pricing, timing, and prep recommendations." />
    </div>
  );
}
